import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormHelperText,
    Box
} from '@mui/material';
import { useState } from 'react';
import {
    ApprovalType,
    CreateCompanyRequest,
    LoginType,
    LowBalanceAudience,
    PaymentSource,
    approvalTypes,
    getLowBalanceAudienceLabel
} from '@/models/company';
import countryList from 'react-select-country-list';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { Validator } from '@/utils/errorHandling';
import { MuiTelInput } from 'mui-tel-input';
import {
    PaymentProvider,
    PaymentSettingsCombination,
    paymentSettingsCombinations
} from '@/models/payment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { currencyOptions } from '@/utils/currencyList';
import {
    useSalaryProvidersQuery,
    useTimeRegistrationProvidersQuery
} from '@/services/paymentsQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { NumericInput } from '@/components/shared/NumericInput';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { StyledDatePicker } from '@/components/shared/DatePicker';
import { Title } from '@/typography/Typography';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';

type AddCompanyDialogProps = {
    open: boolean;
    onClose: () => void;
    onAdd: (
        request: CreateCompanyRequest,
        onSuccess: () => void
    ) => Promise<void>;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
    isLoading?: boolean;
};

export const AddCompanyDialog = (props: AddCompanyDialogProps) => {
    const sortedCurrencyOptions = [...currencyOptions].sort((a, b) =>
        a.label.localeCompare(b.label)
    );

    const countryChoices = countryList()
        .getData()
        .map((item) => ({ label: item.label, value: item.label }));

    const defaultPricePerUser = 19;
    const getInitialEndOfTrial = () => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0, 0, 0, 0); // end of current month
    };

    const [companyName, setCompanyName] = useState<string>('');
    const [currency, setCurrency] = useState<string>('DKK');
    const [country, setCountry] = useState<string>('Denmark');
    const [tenantId, setTenantId] = useState<string>('');
    const [loginType, setLoginType] = useState<LoginType>(LoginType.AzureB2C);
    const [salaryProvider, setSalaryProvider] = useState<string>('Zenegy');
    const [timeRegistrationProvider, setTimeRegistrationProvider] =
        useState<string>('Zenegy');
    const [pricePerUser, setPricePerUser] = useState<number | null>(
        defaultPricePerUser
    );
    const [employeeWithdrawalFee, setEmployeeWithdrawalFee] = useState<
        number | null
    >(0);
    const [companyServiceFee, setCompanyServiceFee] = useState<number | null>(
        0
    );
    const [endOfTrial, setEndOfTrial] = useState<Date | null>(
        getInitialEndOfTrial()
    );
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [paymentCombination, setPaymentCombination] =
        useState<PaymentSettingsCombination>('InstaPaid DKK');
    const [paymentProvider, setPaymentProvider] =
        useState<PaymentProvider>('None');
    const [paymentSource, setPaymentSource] =
        useState<PaymentSource>('InstaPaidAccount');
    const [approvalType, setApprovalType] = useState<ApprovalType>('Manual');
    const [lowBalanceNotification, setLowBalanceNotification] = useState<
        number | null
    >(null);
    const [lowBalanceAudience, setLowBalanceAudience] =
        useState<LowBalanceAudience>('PendingWithdrawalsEmails');
    const [lowBalanceEmail, setLowBalanceEmail] = useState<string>('');
    const [pricePerUserFormatError, setPricePerUserFormatError] =
        useState(false);
    const [withdrawalFeeFormatError, setWithdrawalFeeFormatError] =
        useState(false);
    const [serviceFeeFormatError, setServiceFeeFormatError] = useState(false);
    const [lowBalanceFormatError, setLowBalanceFormatError] = useState(false);
    const hasFormatError =
        pricePerUserFormatError ||
        withdrawalFeeFormatError ||
        serviceFeeFormatError ||
        lowBalanceFormatError;
    const { setAlert } = useNotificationsSnackbar();

    const setPaymentCombinationWithDependancies = (
        newPaymentCombination: PaymentSettingsCombination
    ) => {
        setPaymentCombination(newPaymentCombination);
        if (newPaymentCombination === 'Danske Bank') {
            setPaymentProvider('DanskeBank');
            setPaymentSource('CompanyAccount');
        } else if (newPaymentCombination === 'InstaPaid DKK') {
            setPaymentProvider('None');
            setPaymentSource('InstaPaidAccount');
            setCurrency('DKK');
        } else if (newPaymentCombination === 'InstaPaid EUR') {
            setPaymentProvider('None');
            setPaymentSource('InstaPaidEurAccount');
            setCurrency('EUR');
        } else if (newPaymentCombination === 'Aiia') {
            setPaymentProvider('Aiia');
            setPaymentSource('CompanyAccount');
            setApprovalType('Manual'); // automatic approvals are not available for Aiia
        }
    };

    const dateToUTC = (date: Date) => {
        // make sure we send the data the user sees in the datepicker, ignoring the possible timezone offset
        return new Date(
            Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                17,
                0,
                0
            )
        );
    };

    const clearState = () => {
        setCompanyName('');
        setCurrency('');
        setCountry('');
        setTenantId('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setLowBalanceNotification(null);
        setLowBalanceEmail('');
    };

    const onAdd = () => {
        if (
            !companyName ||
            !currency ||
            !country ||
            !loginType ||
            !firstName ||
            !lastName ||
            !email ||
            !endOfTrial
        ) {
            setAlert(t('company.please_fill_all_fields'), 'error');
            return;
        }
        const lowBalanceNotificationDecimal = lowBalanceNotification;
        const pricePerUserDecimal = pricePerUser;
        const employeeWithdrawalFeeDecimal = employeeWithdrawalFee;
        const companyServiceFeeDecimal = companyServiceFee;

        if (lowBalanceNotificationDecimal === undefined) {
            setAlert(t('company.low_balance_must_be_number'), 'error');
            return;
        }

        if (pricePerUserDecimal === undefined) {
            setAlert('Price per user must be a number', 'error');
            return;
        }

        props.onAdd(
            {
                name: companyName,
                currency: currency,
                country: country,
                tenantId: tenantId,
                loginType: loginType,
                salaryProvider: salaryProvider,
                timeRegistrationProvider: timeRegistrationProvider,
                pricePerUser: pricePerUserDecimal ?? defaultPricePerUser,
                employeeWithdrawalFee: employeeWithdrawalFeeDecimal ?? 0,
                companyServiceFee: companyServiceFeeDecimal ?? 0,
                endOfTrial: dateToUTC(endOfTrial),
                paymentSource: paymentSource,
                paymentProvider: paymentProvider,
                approvalType: approvalType,
                lowBalanceNotificationThreshold: lowBalanceNotificationDecimal,
                lowBalanceAudience: lowBalanceAudience,
                lowBalanceEmail: lowBalanceEmail,
                admin: {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: phoneNumber
                }
            },
            clearState
        );
    };

    const { data: getTimeRegistrationProvidersQuery } =
        useTimeRegistrationProvidersQuery(props.open);

    const { data: getSalaryProvidersQuery } = useSalaryProvidersQuery(
        props.open
    );

    const { t } = useTranslation();

    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <DialogTitle>Add new company</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Fill the fields below to create a new company and the first
                    admin user for this company
                </DialogContentText>

                <InputText
                    label="Company name"
                    value={companyName}
                    error={props.validator.hasError('companyName')}
                    helperText={props.validator.getError('companyName')}
                    onChange={(e) => {
                        setCompanyName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('companyName')
                        );
                    }}
                />

                <Box mt={1}>
                    <Title mb={0.5}>Currency</Title>
                    <Dropdown
                        label="Select currency"
                        choices={sortedCurrencyOptions}
                        value={currency}
                        error={props.validator.hasError('currency')}
                        onChange={(value) => {
                            setCurrency(value || '');
                            props.setValidator(
                                props.validator.clearError('currency')
                            );
                        }}
                        allowClear={false}
                    />
                    {props.validator.hasError('currency') && (
                        <FormHelperText error>
                            {props.validator.getError('currency')}
                        </FormHelperText>
                    )}
                </Box>
                <Box mt={1}>
                    <Title mb={0.5}>Country</Title>
                    <Dropdown
                        label="Select country"
                        choices={countryChoices}
                        value={country}
                        error={props.validator.hasError('country')}
                        onChange={(value) => {
                            setCountry(value || '');
                            props.setValidator(
                                props.validator.clearError('country')
                            );
                        }}
                        allowClear={false}
                    />
                    {props.validator.hasError('country') && (
                        <FormHelperText error>
                            {props.validator.getError('country')}
                        </FormHelperText>
                    )}
                </Box>
                <Box mt={1}>
                    <Title mb={0.5}>Login type</Title>
                    <Dropdown
                        label="Login type"
                        choices={[
                            {
                                label: 'Company login',
                                value: LoginType.AzureAD
                            },
                            {
                                label: 'InstaPaid login',
                                value: LoginType.AzureB2C
                            }
                        ]}
                        value={loginType}
                        error={props.validator.hasError('loginType')}
                        onChange={(value) => {
                            setLoginType(value as LoginType);
                            props.setValidator(
                                props.validator.clearError('loginType')
                            );
                        }}
                        allowClear={false}
                    />
                    {props.validator.hasError('loginType') && (
                        <FormHelperText error>
                            {props.validator.getError('loginType')}
                        </FormHelperText>
                    )}
                </Box>

                {loginType === LoginType.AzureAD && (
                    <InputText
                        label={`${LoginType[loginType]} Tenant ID`}
                        value={tenantId}
                        error={props.validator.hasError('tenantId')}
                        helperText={props.validator.getError('tenantId')}
                        onChange={(e) => {
                            setTenantId(e.currentTarget.value);
                            props.setValidator(
                                props.validator.clearError('tenantId')
                            );
                        }}
                    />
                )}

                <Box mt={1}>
                    <Title mb={0.5}>Salary provider</Title>
                    <Dropdown
                        label="Select salary provider"
                        choices={(
                            getSalaryProvidersQuery?.data?.salaryProviders ?? []
                        ).map((p) => ({ label: p.name, value: p.name }))}
                        value={salaryProvider}
                        error={props.validator.hasError('salaryProvider')}
                        onChange={(value) => {
                            setSalaryProvider(value || '');
                            props.setValidator(
                                props.validator.clearError('salaryProvider')
                            );
                        }}
                        allowClear={false}
                    />
                    {props.validator.hasError('salaryProvider') && (
                        <FormHelperText error>
                            {props.validator.getError('salaryProvider')}
                        </FormHelperText>
                    )}
                </Box>

                <Box mt={1}>
                    <Title mb={0.5}>Time registration provider</Title>
                    <Dropdown
                        label="Select time registration provider"
                        choices={(
                            getTimeRegistrationProvidersQuery?.data
                                ?.providers ?? []
                        ).map((p) => ({ label: p.name, value: p.name }))}
                        value={timeRegistrationProvider}
                        error={props.validator.hasError(
                            'timeRegistrationProvider'
                        )}
                        onChange={(value) => {
                            setTimeRegistrationProvider(value || '');
                            props.setValidator(
                                props.validator.clearError(
                                    'timeRegistrationProvider'
                                )
                            );
                        }}
                        allowClear={false}
                    />
                    {props.validator.hasError('timeRegistrationProvider') && (
                        <FormHelperText error>
                            {props.validator.getError(
                                'timeRegistrationProvider'
                            )}
                        </FormHelperText>
                    )}
                </Box>

                <NumericInput
                    data-testid="price-per-user-input"
                    fullWidth
                    margin="dense"
                    label={t('price_per_user')}
                    helperText={props.validator.getError(
                        'pricePerUser',
                        t('price_for_user_subtitle')
                    )}
                    error={props.validator.hasError('pricePerUser')}
                    value={pricePerUser}
                    onChange={(val) => {
                        setPricePerUser(val);
                        props.setValidator(
                            props.validator.clearError('pricePerUser')
                        );
                    }}
                    onFormatError={setPricePerUserFormatError}
                />

                <NumericInput
                    data-testid="employee-withdrawal-fee-input"
                    fullWidth
                    margin="dense"
                    label={t('fees.withdrawal_fee')}
                    helperText={props.validator.getError(
                        'employeeWithdrawalFee',
                        t('fees.withdrawal_fee_subtitle')
                    )}
                    error={props.validator.hasError('employeeWithdrawalFee')}
                    value={employeeWithdrawalFee}
                    onChange={(val) => {
                        setEmployeeWithdrawalFee(val);
                        props.setValidator(
                            props.validator.clearError('employeeWithdrawalFee')
                        );
                    }}
                    onFormatError={setWithdrawalFeeFormatError}
                />
                <NumericInput
                    data-testid="monthly-service-fee-input"
                    fullWidth
                    margin="dense"
                    label={t('fees.monthly_service_fee')}
                    helperText={props.validator.getError(
                        'companyServiceFee',
                        t('fees.base_fee')
                    )}
                    error={props.validator.hasError('companyServiceFee')}
                    value={companyServiceFee}
                    onChange={(val) => {
                        setCompanyServiceFee(val);
                        props.setValidator(
                            props.validator.clearError('companyServiceFee')
                        );
                    }}
                    onFormatError={setServiceFeeFormatError}
                />

                <Box mt={1}>
                    <Title mb={0.5}>End of trial</Title>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <StyledDatePicker
                            value={dayjs(endOfTrial)}
                            minDate={dayjs()}
                            format="DD/MM/YYYY"
                            onChange={(newValue: Dayjs | null) => {
                                setEndOfTrial(
                                    newValue
                                        ? newValue.toDate()
                                        : getInitialEndOfTrial()
                                );
                                props.setValidator(
                                    props.validator.clearError('endOfTrial')
                                );
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    variant: 'outlined',
                                    error: props.validator.hasError(
                                        'endOfTrial'
                                    ),
                                    helperText:
                                        props.validator.getError('endOfTrial')
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                <Box mt={1}>
                    <Title mb={0.5}>Payout method</Title>
                    <Dropdown
                        label="Select payout method"
                        choices={paymentSettingsCombinations.map((item) => ({
                            label: item,
                            value: item
                        }))}
                        value={paymentCombination}
                        error={
                            props.validator.hasError('paymentSource') ||
                            props.validator.hasError('paymentProvider')
                        }
                        onChange={(value) => {
                            setPaymentCombinationWithDependancies(
                                value as PaymentSettingsCombination
                            );
                            props.setValidator(
                                props.validator.clearError('paymentSource')
                            );
                            props.setValidator(
                                props.validator.clearError('paymentProvider')
                            );
                        }}
                        allowClear={false}
                    />
                    {(props.validator.hasError('paymentSource') ||
                        props.validator.hasError('paymentProvider')) && (
                        <FormHelperText error>
                            {props.validator.getError('paymentSource')}
                            {props.validator.getError('paymentProvider')}
                        </FormHelperText>
                    )}
                </Box>

                {paymentCombination !== 'Aiia' && (
                    <Box mt={1}>
                        <Title mb={0.5}>Approval type</Title>
                        <Dropdown
                            label="Select approval type"
                            choices={approvalTypes.map((item) => ({
                                label: item,
                                value: item
                            }))}
                            value={approvalType}
                            error={props.validator.hasError('approvalType')}
                            onChange={(value) => {
                                setApprovalType(value as ApprovalType);
                                props.setValidator(
                                    props.validator.clearError('approvalType')
                                );
                            }}
                            allowClear={false}
                        />
                        <FormHelperText
                            error={props.validator.hasError('approvalType')}
                        >
                            {props.validator.getError(
                                'approvalType',
                                "If you choose manual, you'll need to manually approve each withdrawal request made by your employees. Auto-approvals are only supported by DanskeBank."
                            )}
                        </FormHelperText>
                    </Box>
                )}

                {(paymentSource === 'InstaPaidAccount' ||
                    paymentSource === 'InstaPaidEurAccount') && (
                    <>
                        <NumericInput
                            fullWidth
                            margin="dense"
                            label={t('company.low_balance_notification')}
                            helperText={props.validator.getError(
                                'lowBalanceNotification',
                                "If the company balance reaches below this number, you'll receive an email remainder to deposit funds"
                            )}
                            error={props.validator.hasError(
                                'lowBalanceNotification'
                            )}
                            value={lowBalanceNotification}
                            onChange={(val) => {
                                setLowBalanceNotification(val);
                                props.setValidator(
                                    props.validator.clearError(
                                        'lowBalanceNotification'
                                    )
                                );
                            }}
                            onFormatError={setLowBalanceFormatError}
                        />

                        <Box mt={1}>
                            <Title mb={0.5}>
                                Low balance notification audience
                            </Title>
                            <Dropdown
                                label="Select audience"
                                choices={(
                                    [
                                        'AllAdmins',
                                        'PendingWithdrawalsEmails',
                                        'Custom'
                                    ] as LowBalanceAudience[]
                                ).map((item) => ({
                                    label: getLowBalanceAudienceLabel(item),
                                    value: item
                                }))}
                                value={lowBalanceAudience}
                                error={props.validator.hasError(
                                    'lowBalanceAudience'
                                )}
                                onChange={(value) => {
                                    setLowBalanceAudience(
                                        value as LowBalanceAudience
                                    );
                                    props.setValidator(
                                        props.validator.clearError(
                                            'lowBalanceAudience'
                                        )
                                    );
                                }}
                                allowClear={false}
                            />
                            <FormHelperText
                                error={props.validator.hasError(
                                    'lowBalanceAudience'
                                )}
                            >
                                {props.validator.getError(
                                    'lowBalanceAudience',
                                    'Choose who should receive emails about your company account balance running low'
                                )}
                            </FormHelperText>
                        </Box>

                        {lowBalanceAudience === 'Custom' && (
                            <InputText
                                label="Emails to receive the notification about low company balance (comma-separated)"
                                helperText={props.validator.getError(
                                    'lowBalanceEmail',
                                    'This email address(-es) will receive the email notifications if your company balance reaches below the threshold above'
                                )}
                                error={props.validator.hasError(
                                    'lowBalanceEmail'
                                )}
                                value={lowBalanceEmail}
                                onChange={(e) => {
                                    setLowBalanceEmail(e.currentTarget.value);
                                    props.setValidator(
                                        props.validator.clearError(
                                            'lowBalanceEmail'
                                        )
                                    );
                                }}
                            />
                        )}
                    </>
                )}

                <Title mt={3} mb={0}>
                    Admin user details:
                </Title>

                <InputText
                    label="First name"
                    value={firstName}
                    error={props.validator.hasError('admin.FirstName')}
                    helperText={props.validator.getError('admin.FirstName')}
                    onChange={(e) => {
                        setFirstName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.FirstName')
                        );
                    }}
                />

                <InputText
                    label="Last name"
                    value={lastName}
                    error={props.validator.hasError('admin.LastName')}
                    helperText={props.validator.getError('admin.LastName')}
                    onChange={(e) => {
                        setLastName(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.LastName')
                        );
                    }}
                />

                <InputText
                    label="Email Address"
                    type="email"
                    value={email}
                    error={props.validator.hasError('admin.Email')}
                    helperText={props.validator.getError('admin.Email')}
                    onChange={(e) => {
                        setEmail(e.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('admin.Email')
                        );
                    }}
                />

                <MuiTelInput
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    name="phone_number"
                    label="Phone number"
                    placeholder="+XX XXXXXX"
                    error={props.validator.hasError('admin.PhoneNumber')}
                    helperText={props.validator.getError(
                        'admin.PhoneNumber',
                        'Phone number including country code'
                    )}
                    value={phoneNumber}
                    onChange={(e) => {
                        setPhoneNumber(e);
                        props.setValidator(
                            props.validator.clearError('admin.PhoneNumber')
                        );
                    }}
                />
            </DialogContent>
            <DialogActions>
                <SecondaryButtonRegular
                    onClick={props.onClose}
                    disabled={props.isLoading}
                >
                    Cancel
                </SecondaryButtonRegular>
                <PrimaryButton
                    onClick={onAdd}
                    disabled={props.isLoading || hasFormatError}
                >
                    Add
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};
